import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Trophy, MapPin, User, Clock, Target } from "lucide-react";
import { skillLevelConfig } from "@/lib/skillLevels";
import { ClubPicker } from "@/components/ClubPicker";

const playerFormSchema = z.object({
  firstName: z.string().min(1, "El nombre es requerido"),
  lastName: z.string().min(1, "El apellido es requerido"),
  dateOfBirth: z.string().min(1, "La fecha de nacimiento es requerida").refine((val) => {
    const date = new Date(val);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date <= today;
  }, "La fecha de nacimiento no puede ser en el futuro"),
  gender: z.enum(["male", "female", "other", "prefer_not_to_say"], {
    required_error: "El género es requerido",
  }),
  country: z.string().min(1, "El país es requerido"),
  city: z.string().min(1, "La ciudad es requerida"),
  homeClubId: z.string().optional().nullable(),
  skillLevel: z.number().min(1).max(5).default(2),
  yearsPlaying: z.number().min(0).max(50).default(0),
  playingHand: z.enum(["right", "left", "ambidextrous"], {
    required_error: "La mano dominante es requerida",
  }),
  playerType: z.enum(["offensive", "defensive", "all_around"], {
    required_error: "El estilo de juego es requerido",
  }),
  preferredPlayTimes: z.array(z.string()).min(1, "Selecciona al menos un horario preferido"),
  playingFrequency: z.enum(["once_week", "twice_week", "three_plus_week", "daily"], {
    required_error: "La frecuencia de juego es requerida",
  }),
  termsAccepted: z
    .boolean()
    .refine((v) => v === true, {
      message: "Debes aceptar los Términos y la Política de Privacidad",
    }),
});

const TERMS_VERSION = "v1.1";

type PlayerFormData = z.infer<typeof playerFormSchema>;

type PlayerCompletionData = Omit<PlayerFormData, "termsAccepted"> & {
  termsAcceptedVersion: string;
  termsAcceptedAt: string;
};

interface PlayerOnboardingProps {
  onComplete: (data: PlayerCompletionData) => void;
  isSubmitting?: boolean;
  userName?: string;
  onCancel?: () => void;
}

const playTimeOptions = [
  { id: "morning", label: "Mañana (6-12)" },
  { id: "afternoon", label: "Tarde (12-18)" },
  { id: "evening", label: "Noche (18-23)" },
  { id: "weekend", label: "Fines de semana" },
];

const countryOptions = [
  { code: "DO", name: "República Dominicana" },
  { code: "PR", name: "Puerto Rico" },
  { code: "MX", name: "México" },
  { code: "CO", name: "Colombia" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "PE", name: "Perú" },
  { code: "VE", name: "Venezuela" },
  { code: "EC", name: "Ecuador" },
  { code: "PA", name: "Panamá" },
  { code: "CR", name: "Costa Rica" },
  { code: "GT", name: "Guatemala" },
  { code: "CU", name: "Cuba" },
  { code: "UY", name: "Uruguay" },
  { code: "PY", name: "Paraguay" },
  { code: "BO", name: "Bolivia" },
  { code: "HN", name: "Honduras" },
  { code: "SV", name: "El Salvador" },
  { code: "NI", name: "Nicaragua" },
  { code: "US", name: "Estados Unidos" },
  { code: "ES", name: "España" },
  { code: "BR", name: "Brasil" },
  { code: "OTHER", name: "Otro país" },
];

export function PlayerOnboarding({ onComplete, isSubmitting, userName, onCancel }: PlayerOnboardingProps) {
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const form = useForm<PlayerFormData>({
    resolver: zodResolver(playerFormSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      country: "",
      city: "",
      homeClubId: null,
      skillLevel: 2,
      yearsPlaying: 0,
      preferredPlayTimes: [],
      termsAccepted: false,
    },
  });

  const onSubmit = (data: PlayerFormData) => {
    const { termsAccepted: _ignored, ...rest } = data;
    const payload: PlayerCompletionData = {
      ...rest,
      termsAcceptedVersion: TERMS_VERSION,
      termsAcceptedAt: new Date().toISOString(),
    };
    onComplete(payload);
  };

  const nextStep = async () => {
    if (step < totalSteps) {
      // Define fields to validate for each step
      const fieldsToValidate: Record<number, (keyof PlayerFormData)[]> = {
        1: ['firstName', 'lastName', 'dateOfBirth', 'gender', 'country', 'city'],
        2: ['skillLevel', 'yearsPlaying', 'playingHand', 'playerType'],
        3: ['preferredPlayTimes', 'playingFrequency', 'termsAccepted'],
      };

      const isValid = await form.trigger(fieldsToValidate[step]);
      if (isValid) {
        setStep(step + 1);
      }
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center relative">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onCancel}
              className="absolute left-4 top-4"
              data-testid="button-cancel-onboarding"
            >
              <span className="text-lg">←</span>
            </Button>
          )}
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Trophy className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {userName ? `¡Bienvenido, ${userName}!` : "¡Bienvenido a CourtVibe!"}
          </CardTitle>
          <CardDescription>
            Completa tu perfil de jugador para empezar a conectar con otros jugadores de pádel
          </CardDescription>
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-12 rounded-full transition-colors ${
                  i + 1 <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <User className="h-5 w-5 text-primary" />
                    <span>Información Personal</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nombre *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Tu nombre" data-testid="input-first-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Apellido *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Tu apellido" data-testid="input-last-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>País *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-country">
                              <SelectValue placeholder="Selecciona tu país" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {countryOptions.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Para el ranking de tu país
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ciudad *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input {...field} placeholder="Ej: Santo Domingo" className="pl-10" data-testid="input-city" />
                          </div>
                        </FormControl>
                        <FormDescription>
                          Para encontrar jugadores cerca de ti
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="homeClubId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mi club (opcional)</FormLabel>
                        <FormControl>
                          <ClubPicker
                            value={field.value || null}
                            onChange={(id) => field.onChange(id)}
                            country={form.watch("country") || null}
                            testId="onboarding-club-picker"
                          />
                        </FormControl>
                        <FormDescription>
                          Donde sueles jugar. Podrás cambiarlo más adelante.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Género *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-gender">
                                <SelectValue placeholder="Seleccionar" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="male">Masculino</SelectItem>
                              <SelectItem value="female">Femenino</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  </div>

                  <FormField
                    control={form.control}
                    name="dateOfBirth"
                    render={({ field }) => {
                      const [localYear, setLocalYear] = useState(
                        field.value ? field.value.split('-')[0] : ''
                      );
                      const [localMonth, setLocalMonth] = useState(
                        field.value ? field.value.split('-')[1] : ''
                      );
                      const [localDay, setLocalDay] = useState(
                        field.value ? field.value.split('-')[2] : ''
                      );
                      
                      const currentYear = new Date().getFullYear();
                      const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
                      const months = [
                        { value: '01', label: 'Enero' },
                        { value: '02', label: 'Febrero' },
                        { value: '03', label: 'Marzo' },
                        { value: '04', label: 'Abril' },
                        { value: '05', label: 'Mayo' },
                        { value: '06', label: 'Junio' },
                        { value: '07', label: 'Julio' },
                        { value: '08', label: 'Agosto' },
                        { value: '09', label: 'Septiembre' },
                        { value: '10', label: 'Octubre' },
                        { value: '11', label: 'Noviembre' },
                        { value: '12', label: 'Diciembre' },
                      ];
                      const days = Array.from({ length: 31 }, (_, i) =>
                        String(i + 1).padStart(2, '0')
                      );

                      const handleYearChange = (value: string) => {
                        setLocalYear(value);
                        const dateStr = `${value}-${localMonth}-${localDay}`;
                        field.onChange(dateStr);
                      };

                      const handleMonthChange = (value: string) => {
                        setLocalMonth(value);
                        const dateStr = `${localYear}-${value}-${localDay}`;
                        field.onChange(dateStr);
                      };

                      const handleDayChange = (value: string) => {
                        setLocalDay(value);
                        const dateStr = `${localYear}-${localMonth}-${value}`;
                        field.onChange(dateStr);
                      };

                      return (
                        <FormItem>
                          <FormLabel>Fecha de nacimiento *</FormLabel>
                          <div className="grid grid-cols-3 gap-2">
                            <Select value={localYear} onValueChange={handleYearChange}>
                              <FormControl>
                                <SelectTrigger data-testid="select-birth-year">
                                  <SelectValue placeholder="Año" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {years.map((y) => (
                                  <SelectItem key={y} value={String(y)}>
                                    {y}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select value={localMonth} onValueChange={handleMonthChange}>
                              <FormControl>
                                <SelectTrigger data-testid="select-birth-month">
                                  <SelectValue placeholder="Mes" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {months.map((m) => (
                                  <SelectItem key={m.value} value={m.value}>
                                    {m.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select value={localDay} onValueChange={handleDayChange}>
                              <FormControl>
                                <SelectTrigger data-testid="select-birth-day">
                                  <SelectValue placeholder="Día" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {days.map((d) => (
                                  <SelectItem key={d} value={d}>
                                    {d}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <Target className="h-5 w-5 text-primary" />
                    <span>Tu Nivel de Juego</span>
                  </div>

                  <FormField
                    control={form.control}
                    name="skillLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Selecciona tu nivel actual *</FormLabel>
                        <FormControl>
                          <div className="grid gap-3">
                            {[1, 2, 3, 4, 5].map((level) => {
                              const config = skillLevelConfig[level];
                              const isSelected = field.value === level;
                              return (
                                <div
                                  key={level}
                                  onClick={() => field.onChange(level)}
                                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover-elevate ${
                                    isSelected 
                                      ? 'border-primary bg-primary/10' 
                                      : 'border-border hover:border-primary/50'
                                  }`}
                                  data-testid={`skill-level-${level}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                        isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                                      }`}>
                                        {level}
                                      </div>
                                      <div>
                                        <p className="font-semibold">{config.name}</p>
                                        <p className="text-sm text-muted-foreground">{config.description}</p>
                                      </div>
                                    </div>
                                    <Badge variant={isSelected ? "default" : "outline"} className="text-xs">
                                      Nivel: {config.initialLevel}
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </FormControl>
                        <FormDescription>
                          Tu nivel inicial va de 1.0 (Iniciación) a 5.0 (Muy Avanzado). Con tus resultados en partidos, puede subir hasta 7.0.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="yearsPlaying"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Años jugando</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              min={0}
                              max={50}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                              data-testid="input-years-playing"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="playingHand"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mano dominante *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-playing-hand">
                                <SelectValue placeholder="Seleccionar" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="right">Diestro</SelectItem>
                              <SelectItem value="left">Zurdo</SelectItem>
                              <SelectItem value="ambidextrous">Ambidiestro</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="playerType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estilo de juego *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-player-type">
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="offensive">Ofensivo - Me gusta atacar</SelectItem>
                            <SelectItem value="defensive">Defensivo - Prefiero defender</SelectItem>
                            <SelectItem value="all_around">Completo - Me adapto al juego</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-lg font-medium mb-4">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Disponibilidad y Preferencias</span>
                  </div>

                  <FormField
                    control={form.control}
                    name="preferredPlayTimes"
                    render={() => (
                      <FormItem>
                        <FormLabel>¿Cuándo prefieres jugar? *</FormLabel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                          {playTimeOptions.map((option) => (
                            <FormField
                              key={option.id}
                              control={form.control}
                              name="preferredPlayTimes"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(option.id)}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        if (checked) {
                                          field.onChange([...current, option.id]);
                                        } else {
                                          field.onChange(current.filter((v) => v !== option.id));
                                        }
                                      }}
                                      data-testid={`checkbox-time-${option.id}`}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">{option.label}</FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="playingFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>¿Con qué frecuencia juegas? *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-frequency">
                              <SelectValue placeholder="Seleccionar frecuencia" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="once_week">1 vez por semana</SelectItem>
                            <SelectItem value="twice_week">2 veces por semana</SelectItem>
                            <SelectItem value="three_plus_week">3+ veces por semana</SelectItem>
                            <SelectItem value="daily">Todos los días</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="termsAccepted"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start gap-3 rounded-lg border p-4 bg-muted/30">
                        <FormControl>
                          <Checkbox
                            checked={field.value === true}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                            data-testid="checkbox-accept-terms"
                            className="mt-0.5"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-snug">
                          <FormLabel className="font-normal cursor-pointer text-sm">
                            Acepto los{" "}
                            <a
                              href="/terminos"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline text-primary"
                              data-testid="link-onboarding-terms"
                            >
                              Términos
                            </a>{" "}
                            y la{" "}
                            <a
                              href="/privacidad"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline text-primary"
                              data-testid="link-onboarding-privacy"
                            >
                              Política de Privacidad
                            </a>{" "}
                            de CourtVibe. Confirmo que tengo al menos 16 años.
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button type="button" variant="outline" onClick={prevStep} data-testid="button-prev">
                    Anterior
                  </Button>
                ) : (
                  <div />
                )}

                {step < totalSteps ? (
                  <Button type="button" onClick={nextStep} data-testid="button-next">
                    Siguiente
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting} data-testid="button-submit">
                    {isSubmitting ? "Guardando..." : "Completar Perfil"}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
